import { useNavigate } from '@tanstack/react-router';
import { api, call, type Schemas } from '../../api/client';
import { useAction, useNotifications } from '../../api/queries';
import { useHousehold } from '../../api/session';
import { Button, Card, EmptyState, PageHeader, Spinner } from '../../components/ui';
import { timeAgo } from '../../lib/format';

type Item = Schemas['NotificationListResponse']['items'][number];

export function NotificationsPage() {
  const { familyId } = useHousehold();
  const q = useNotifications();
  const navigate = useNavigate();
  const markAll = useAction(() => call(api.POST('/api/v1/families/{familyId}/notifications/read-all', { params: { path: { familyId } } })));
  const markRead = useAction((id: string) => call(api.POST('/api/v1/families/{familyId}/notifications/{notificationId}/read', { params: { path: { familyId, notificationId: id } } })));

  const open = (n: Item) => {
    if (!n.read) markRead.mutate(n.id);
    if (n.subjectType === 'ACTIVITY' && n.subjectId) return navigate({ to: '/activities/$activityId', params: { activityId: n.subjectId } });
    if (n.subjectType === 'CHILD_DELEGATION' && n.subjectId) return navigate({ to: '/decisions/delegations/$proposalId', params: { proposalId: n.subjectId } });
    if (n.subjectType === 'PRIORITY_CHANGE_REQUEST' && n.subjectId) return navigate({ to: '/decisions/priority/$requestId', params: { requestId: n.subjectId } });
    if (n.taskId) return navigate({ to: '/tasks/$taskId', params: { taskId: n.taskId } });
    if (n.seriesId) return navigate({ to: '/recurring/$seriesId', params: { seriesId: n.seriesId } });
    if (n.subjectType === 'CHILD' && n.subjectId) return navigate({ to: '/children/$childId', params: { childId: n.subjectId } });
    return undefined;
  };

  if (!q.data) return <Spinner />;
  return (
    <>
      <PageHeader title="Notifications" actions={q.data.items.some((n) => !n.read) ? <Button variant="secondary" size="sm" busy={markAll.isPending} onClick={() => markAll.mutate(undefined)}>Mark all read</Button> : null} />
      {q.data.items.length === 0 ? <EmptyState title="You're all caught up" /> : (
        <ul className="space-y-2">
          {q.data.items.map((n) => (
            <li key={n.id}>
              <button className="w-full text-left" onClick={() => void open(n)}>
                <Card className={`flex items-start gap-3 text-sm hover:bg-[var(--surface-2)] ${n.read ? 'opacity-70' : ''}`}>
                  {!n.read ? <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[var(--accent)]" aria-label="Unread" /> : <span className="w-2" />}
                  <span className="flex-1">{n.text}</span>
                  <span className="shrink-0 text-xs text-[var(--muted)]">{timeAgo(n.createdAt)}</span>
                </Card>
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
